import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import Navbar from "../../components/navBar";

const API = process.env.REACT_APP_API_URL;
const CURRENT_YEAR = 2025;

const COLUMNS = [
  { key: "played", label: "P", description: "Played" },
  { key: "won", label: "W", description: "Won" },
  { key: "drawn", label: "D", description: "Drawn" },
  { key: "lost", label: "L", description: "Lost" },
  { key: "goalsFor", label: "GF", description: "Goals for" },
  { key: "goalsAgainst", label: "GA", description: "Goals against" },
  { key: "goalDifference", label: "GD", description: "Goal difference" },
  { key: "points", label: "Pts", description: "Points" },
];

function getList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}

const LeagueTable = () => {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );

  const fetchLeagues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/leagues`);
      const leagueRows = getList(response.data);
      setLeagues(leagueRows);
      setSelectedLeague((current) => {
        if (current && leagueRows.some((league) => String(league.leagueId) === String(current.leagueId))) {
          return current;
        }
        return leagueRows[0] || null;
      });
      if (leagueRows.length === 0) {
        setStandings([]);
        setLoading(false);
      }
    } catch (fetchError) {
      console.error("Error fetching leagues:", fetchError);
      setError("We couldn't load the available leagues. Please try again.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem("desac-sidebar-collapsed") === "true");
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === "boolean"
          ? event.detail.collapsed
          : window.localStorage.getItem("desac-sidebar-collapsed") === "true"
      );
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("storage", updateCollapsedState);
    window.addEventListener("desac-sidebar-toggle", updateCollapsedState);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("storage", updateCollapsedState);
      window.removeEventListener("desac-sidebar-toggle", updateCollapsedState);
    };
  }, []);

  useEffect(() => {
    if (!selectedLeague?.leagueId) return undefined;
    let active = true;
    const loadStandings = async () => {
      setLoading(true);
      setError(null);
      setStandings([]);
      try {
        await axios.post(`${API}/league-table/initialize/${selectedLeague.leagueId}/${CURRENT_YEAR}`);
        const response = await axios.get(
          `${API}/league-table/standings/${selectedLeague.leagueId}/${CURRENT_YEAR}`
        );
        if (active) setStandings(getList(response.data));
      } catch (loadError) {
        console.error("Error loading league standings:", loadError);
        if (active) {
          setError("We couldn't load standings for this league. Please try again.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadStandings();
    return () => {
      active = false;
    };
  }, [selectedLeague]);

  const sidebarWidth = isDesktop && isCollapsed ? "5rem" : "16rem";

  const retry = () => {
    if (leagues.length === 0) {
      fetchLeagues();
      return;
    }
    const league = selectedLeague;
    setSelectedLeague(null);
    window.setTimeout(() => setSelectedLeague(league), 0);
  };

  return (
    <>
      <Navbar />
      <main
        style={{ "--tables-sidebar-width": sidebarWidth }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--tables-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <TrophyIcon aria-hidden="true" className="h-4 w-4" />
                MATCH CENTRE
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">League tables</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Follow club performance and standings across the current season.
              </p>
            </div>
            <div aria-live="polite" className="inline-flex min-h-10 w-fit items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300">
              <TrophyIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
              {loading ? "Updating standings…" : error ? "Standings unavailable" : `${standings.length} ${standings.length === 1 ? "club" : "clubs"}`}
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          <section aria-label="League and season selection" className="mb-6 rounded-xl border border-line bg-surface-900 p-4 sm:p-5">
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <label className="block min-w-0">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">Competition</span>
                <span className="relative block">
                  <select
                    aria-label="Select league"
                    value={selectedLeague?.leagueId ?? ""}
                    onChange={(event) => {
                      const league = leagues.find((item) => String(item.leagueId) === event.target.value);
                      if (league) setSelectedLeague(league);
                    }}
                    disabled={!leagues.length}
                    className="field min-h-11 appearance-none pr-10 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {!leagues.length && <option value="">No leagues available</option>}
                    {leagues.map((league) => (
                      <option key={league.leagueId} value={league.leagueId}>{league.name}</option>
                    ))}
                  </select>
                  <ChevronDownIcon aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                </span>
              </label>
              <div className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300 sm:min-w-[180px]">
                <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Season</span>
                <span className="ml-auto font-semibold tabular-nums">{CURRENT_YEAR}/{CURRENT_YEAR + 1}</span>
              </div>
            </div>
          </section>

          {error ? (
            <section role="alert" className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <TrophyIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">Standings are temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-400">{error}</p>
              <button type="button" onClick={retry} className="btn-primary mt-5 min-h-11 gap-2">
                <ArrowPathIcon aria-hidden="true" className="h-4 w-4" /> Try again
              </button>
            </section>
          ) : loading ? (
            <div role="status" aria-label="Loading league standings" className="flex min-h-72 items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
              <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
              Loading league standings…
            </div>
          ) : standings.length > 0 ? (
            <section aria-labelledby="standings-title" className="overflow-hidden rounded-xl border border-line bg-surface-850">
              <div className="flex flex-col gap-3 border-b border-line bg-surface-900 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                <div className="min-w-0">
                  <p className="eyebrow-accent">CURRENT STANDINGS</p>
                  <h2 id="standings-title" className="mt-1 truncate text-lg font-semibold text-white sm:text-xl">
                    {selectedLeague?.name || "League"}
                  </h2>
                </div>
                <span className="inline-flex w-fit items-center gap-1.5 rounded-md border border-line bg-surface-850 px-2.5 py-1.5 text-xs font-medium text-gray-400">
                  <ShieldCheckIcon aria-hidden="true" className="h-4 w-4 text-gray-500" />
                  Season table
                </span>
              </div>

              <div className="border-b border-line bg-surface-900/70 px-4 py-2.5 text-xs text-gray-500 sm:px-5">
                Scroll horizontally to view all statistics.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-separate border-spacing-0 text-sm">
                  <caption className="sr-only">
                    {selectedLeague?.name || "League"} standings for {CURRENT_YEAR}/{CURRENT_YEAR + 1}
                  </caption>
                  <thead>
                    <tr className="bg-surface-600 text-xs font-semibold uppercase tracking-wider text-gray-300">
                      <th scope="col" className="sticky left-0 z-20 w-16 border-b border-line px-3 py-3 text-center bg-surface-600">Pos</th>
                      <th scope="col" className="sticky left-16 z-20 min-w-[210px] border-b border-line px-4 py-3 text-left bg-surface-600">Club</th>
                      {COLUMNS.map((column) => (
                        <th key={column.key} scope="col" title={column.description} className={`min-w-[58px] border-b border-line px-3 py-3 text-right ${column.key === "points" ? "text-accent-soft" : ""}`}>
                          <span aria-label={column.description}>{column.label}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((team, index) => (
                      <StandingRow key={team.id ?? `${team.clubId ?? team.club?.clubId ?? "club"}-${team.position ?? index}`} team={team} index={index} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <section className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <TrophyIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">No standings available</h2>
              <p className="mt-2 text-sm text-gray-400">
                {selectedLeague ? `There are no standings to show for ${selectedLeague.name} in this season.` : "Choose a league to view its standings."}
              </p>
            </section>
          )}
        </div>
      </main>
    </>
  );
};

function StandingRow({ team, index }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const club = team.club || {};
  const clubName = club.name || "Club information unavailable";
  const clubId = club.clubId ?? team.clubId;
  const initials = clubName.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();

  return (
    <tr className="group transition-colors hover:bg-surface-800 focus-within:bg-surface-800">
      <td className="sticky left-0 z-10 border-b border-line bg-surface-850 px-3 py-3 text-center tabular-nums text-gray-400 transition-colors group-hover:bg-surface-800">
        <span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${index === 0 ? "bg-accent/10 text-accent-soft" : "text-gray-400"}`}>
          {displayValue(team.position)}
        </span>
      </td>
      <td className="sticky left-16 z-10 border-b border-line bg-surface-850 px-4 py-3 transition-colors group-hover:bg-surface-800">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-surface-800">
            {club.logo && !logoFailed ? (
              <img src={club.logo} alt="" loading="lazy" onError={() => setLogoFailed(true)} className="h-full w-full object-contain p-1" />
            ) : (
              <span aria-hidden="true" className="text-[10px] font-bold text-gray-500">{initials || "?"}</span>
            )}
          </span>
          {clubId ? (
            <Link to={`/sports/club/${clubId}`} className="min-w-0 truncate font-medium text-gray-200 transition-colors hover:text-accent-soft focus-visible:rounded-sm">
              {clubName}
            </Link>
          ) : (
            <span className="min-w-0 truncate font-medium text-gray-200">{clubName}</span>
          )}
        </div>
      </td>
      {COLUMNS.map((column) => (
        <td key={column.key} className={`border-b border-line px-3 py-3 text-right tabular-nums ${column.key === "points" ? "font-bold text-accent-soft" : "text-gray-400"}`}>
          {displayValue(team[column.key])}
        </td>
      ))}
    </tr>
  );
}

export default LeagueTable;
